import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiCallsActions } from '../app/stores/apicalls-slice';
import { AppDispatch, RootState } from '../app/stores/store';
import { code } from '../components/basic/code';
import { api } from './api';
import { apiConfig } from './config';

export { useApi };

function useApi<T = any>(
    urlTemplate: string,
    options?: { persist?: boolean; skipResult?: boolean; muted?: boolean }
) {
    //id?: string if (!id) id = url;
    const method = 'post';
    const dispatch = useDispatch<AppDispatch>();
    const apiCalls = useSelector((state: RootState) => state.apiCalls);
    const apiInfo = apiCalls.calls.find(x => x.id === urlTemplate);

    useEffect(() => {
        // if (!options?.persist && !options?.muted && !!apiInfo) {
        //     throw new Error(`Duplicate useAPI("${urlTemplate}")`);
        // }
        dispatch(apiCallsActions.add(urlTemplate));

        // return () => {
        //     !options?.persist && !options?.muted && dispatch(apiCallsActions.remove(urlTemplate));
        // };
    }, []);

    const dispose = () => {
        dispatch(apiCallsActions.remove(urlTemplate));
    };

    const updateResult = (result: T) => {
        dispatch(apiCallsActions.updateResult({ id: urlTemplate, result }));
    };

    const reset = () => {
        dispatch(apiCallsActions.reset(urlTemplate));
    };

    const remove = (query?: any, onSuccess?: VoidFunction, onError?: VoidFunction) => {
        if (options?.muted) {
            throw new Error(`Muted useAPI("${urlTemplate}")`);
        }
        dispatch(apiCallsActions.setBusy(urlTemplate));
        let api_url = urlTemplate;
        if (query) {
            code.getProperties(query).forEach(p => {
                const v = query[p] ?? '';
                api_url = api_url.replace(`{${p}}`, v);
            });
        }

        return api
            .call('delete', `${apiConfig.host}${api_url}`)
            .then(result => {
                dispatch(
                    apiCallsActions.setSuccess({
                        id: urlTemplate,
                        result: options?.skipResult ? undefined : result,
                    })
                );

                onSuccess && onSuccess();
            })
            .catch(error => {
                dispatch(apiCallsActions.setError({ id: urlTemplate, error }));
                onError && onError();
                return Promise.reject(error);
            });
    };

    const get = (query?: any) => {
        if (options?.muted) throw new Error(`Muted useAPI("${urlTemplate}")`);

        dispatch(apiCallsActions.setBusy(urlTemplate));
        let api_url = urlTemplate;
        if (query) {
            code.getProperties(query).forEach(p => {
                const v = query[p] ?? '';
                api_url = api_url.replace(`{${p}}`, v);
            });
        }

        return api
            .call('get', `${apiConfig.host}${api_url}`)
            .then(result => {
                dispatch(
                    apiCallsActions.setSuccess({
                        id: urlTemplate,
                        result: options?.skipResult ? undefined : result,
                    })
                );
                return result;
            })
            .catch(error => {
                dispatch(apiCallsActions.setError({ id: urlTemplate, error }));
                return Promise.reject(error);
            });
    };

    const post = (
        query?: any,
        payload?: any,
        resolve?: (result: T) => void,
        reject?: (reason: any) => void
    ) => {
        if (options?.muted) {
            throw new Error(`Muted useAPI("${urlTemplate}")`);
        }
        dispatch(apiCallsActions.setBusy(urlTemplate));
        let api_url = urlTemplate;
        if (query) {
            code.getProperties(query).forEach(p => {
                const v = query[p] ?? '';
                api_url = api_url.replace(`{${p}}`, v);
            });
        }
        return api
            .call(method, `${apiConfig.host}${api_url}`, payload)
            .then(result => {
                dispatch(
                    apiCallsActions.setSuccess({
                        id: urlTemplate,
                        result: options?.skipResult ? undefined : result,
                    })
                );
                resolve && resolve(result);
                return result;
            })
            .catch(error => {
                dispatch(apiCallsActions.setError({ id: urlTemplate, error }));
                reject && reject(error);
                return Promise.reject(error);
            });
    };

    const callApi = (query?: any, payload?: any) => {
        return new Promise<T>((resolve, reject) => {
            if (!resolve) resolve = () => {};
            post(query, payload, resolve, reject);
        });
    };

    return {
        initialized: apiInfo?.initialized,
        initializing: apiInfo?.initializing,
        success: apiInfo?.success,
        busy: apiInfo?.busy,
        error: apiInfo?.error,
        result: apiInfo?.result as T,
        remove,
        get,
        post,
        callApi,
        updateResult,
        reset,
        dispose,
    };
}
